import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import cx from 'classnames';
import { withRouter } from 'react-router-dom';
import type { RouteComponentProps } from 'react-router-dom';

// Actions
import UserActions from 'app/shared/flux/actions/UserActions';

// Components
import ChatButton from 'app/shared/modules/hdp/ChatButton';
import ContactForm from 'app/shared/modules/hdp/form/Contact/ContactForm';
import ContactListedBy from 'app/shared/modules/hdp/form/Contact/ContactListedBy';
import ContactOverlay from 'app/shared/modules/hdp/form/Contact/ContactOverlay';
import Disclaimer from 'app/shared/modules/hdp/form/Disclaimer';
import LandlordInfo from 'app/shared/modules/hdp/form/Contact/LandlordInfo';
import SatelliteMsgStatus from 'app/shared/modules/satellite/SatelliteMsgStatus';

// Misc / Utils
import {
  contactStyles,
  containerStyles,
  contentStyles,
  contentWrapperStyles,
  disclaimerStyles,
} from './Contact.styles';
import type {
  ContactFormLeadTriggerObject,
  ContactFormTriggerLocation,
} from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import { emitTrackModalContactFormViewEvent } from 'app/shared/models/Clickstream/ContactClickstreamEvents';
import queryUtils from 'app/shared/utils/queryUtils';
import { noop } from 'lodash';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { UserState } from 'app/shared/flux/reducers/user';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { Dispatch } from 'redux';
import { type ContactFormData, useContactSubmission } from 'app/shared/hooks/useContactSubmission';

interface Conversations {
  [aliasEncoded: string]: {
    satelliteEnabled: boolean;
    [key: string]: unknown;
  };
}

interface UnitDetails {
  modelId?: string;
  [key: string]: unknown;
}

interface ContactOwnProps {
  className?: string;
  clickstreamEvent?: {
    triggerLocation?: ContactFormTriggerLocation;
    triggerObject?: ContactFormLeadTriggerObject;
  };
  clientLoaded?: boolean;
  closePopup?: () => void;
  onClose?: () => void;
  conversations?: Conversations;
  countryCode?: string;
  currentListing?: ListingDetails;
  customForm?: React.ComponentType<{
    isCachedDataPending: boolean;
    clientLoaded: boolean;
    contactSubmitted: boolean;
    emailErrorMessage: string;
    errorMessage: string;
    isLoading: boolean;
    isMobile: boolean;
    isPopup?: boolean;
    listing: ListingDetails;
    onSubmitForm: (values: ContactFormData) => Promise<void> | void;
    phoneErrorMessage: string;
    user: UserState;
    userInquiryDataCache: UserState['inquiryDataCache'];
  }>;
  customTracking?: () => void;
  emailErrorMessage?: string;
  hasShownPostContactSavedSearch?: boolean;
  hdpV3Form?: boolean;
  isCachedDataPending?: boolean;
  isLoggedIn?: boolean;
  isMobile?: boolean;
  isPopup?: boolean;
  listing: ListingDetails;
  onHidePopup?: () => void;
  onShowSuccessModal?: () => void;
  onSubmitForm?: (values: ContactFormData) => Promise<void> | void;
  onSetActiveModal?: (modal: string | null) => void;
  onClickPhoneContact?: () => void;
  pathname?: string;
  phoneErrorMessage?: string;
  renderModal?: () => React.ReactNode;
  dispatch?: Dispatch;
  shouldUseAreaBoundary?: boolean;
  unitDetails?: UnitDetails;
  user?: UserState;
  userInquiryDataCache?: UserState['inquiryDataCache'];
}

type ContactProps = ContactOwnProps & RouteComponentProps;

const Contact = (props: ContactProps) => {
  const dispatch = useDispatch();

  // Get values from Redux store
  const clientLoadedFromStore = useSelector((state: RootReduxState) => state.app.clientLoaded);
  const conversationsFromStore = useSelector((state: RootReduxState) => state.user.conversations);
  const countryCodeFromStore = useSelector((state: RootReduxState) => state.geolocation.area.countryCode);
  const hasShownPostContactSavedSearchFromStore = useSelector(
    (state: RootReduxState) => state.app.hasShownPostContactSavedSearch,
  );
  const isLoggedInFromStore = useSelector((state: RootReduxState) => state.user.loggedIn);
  const isMobileFromStore = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const userFromStore = useSelector((state: RootReduxState) => state.user);
  const userInquiryDataCacheFromStore = useSelector((state: RootReduxState) => state.user.inquiryDataCache);

  // Get pathname and search params from location (withRouter provides these)
  const pathnameFromLocation = props.location?.pathname;
  const shouldUseAreaBoundaryFromLocation = props.location
    ? queryUtils.parse(props.location.search).border !== false
    : true;

  const {
    className,
    clientLoaded = clientLoadedFromStore,
    closePopup,
    onClose,
    conversations = conversationsFromStore,
    countryCode = countryCodeFromStore,
    customForm,
    customTracking,
    hasShownPostContactSavedSearch = hasShownPostContactSavedSearchFromStore,
    hdpV3Form,
    isCachedDataPending,
    isLoggedIn = isLoggedInFromStore,
    isMobile = isMobileFromStore,
    isPopup,
    listing,
    onHidePopup = noop,
    pathname = pathnameFromLocation,
    shouldUseAreaBoundary = shouldUseAreaBoundaryFromLocation,
    unitDetails = {},
    user = userFromStore,
    userInquiryDataCache = userInquiryDataCacheFromStore,
  } = props;

  // Use the contact submission hook for all submission logic
  const {
    handleSubmitForm: hookHandleSubmitForm,
    handlePhoneClick: hookHandlePhoneClick,
    formState,
  } = useContactSubmission({
    listing,
    user,
    dispatch,
    onClose,
    closePopup,
    onHidePopup,
    isPopup,
    isLoggedIn,
    hasShownPostContactSavedSearch,
    customTracking,
    countryCode,
    pathname,
    shouldUseAreaBoundary,
    triggerLocation: props.clickstreamEvent?.triggerLocation,
    triggerObject: props.clickstreamEvent?.triggerObject,
    unitDetails,
  });

  const { userOnWaitlist } = listing;
  const [state, setState] = useState<{
    activeModal: string | null;
    previousInquiryMessagePopup: boolean;
  }>({
    activeModal: null,
    previousInquiryMessagePopup: includes(listing.userItemTypes, 'inquiry') || userOnWaitlist,
  });

  useEffect(() => {
    const { previousInquiryMessagePopup } = state;

    const nextInquiryMessagePopup = includes(listing.userItemTypes, 'inquiry') || userOnWaitlist;
    if (previousInquiryMessagePopup !== nextInquiryMessagePopup) {
      setState((prev) => ({
        ...prev,
        previousInquiryMessagePopup: nextInquiryMessagePopup,
      }));
    }

    if (userInquiryDataCache?.isPending) {
      dispatch(UserActions.inquiry.getMessageCache());
    }

    if (isPopup) {
      emitTrackModalContactFormViewEvent({
        listing,
        triggerLocation: props.clickstreamEvent?.triggerLocation || 'home_details',
        triggerObject: props.clickstreamEvent?.triggerObject || 'home_details_component|modal_contact_form',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetActiveModal = useCallback((modal: string | null = null) => {
    setState((prev) => ({
      ...prev,
      activeModal: modal,
    }));
  }, []);

  // Use the hook's handlers, or allow props to override (for customForm)
  const handlePhoneClick = useCallback(() => {
    if (props.onClickPhoneContact) {
      props.onClickPhoneContact();
    } else {
      hookHandlePhoneClick();
    }
  }, [props, hookHandlePhoneClick]);

  const handleSubmitForm = useCallback(
    async (inputValues: ContactFormData) => {
      // If a custom onSubmitForm is provided via props, use that instead
      if (props.onSubmitForm) {
        await props.onSubmitForm(inputValues);
        return;
      }

      // Otherwise use the hook's handler (which manages isSuccessPopupVisible)
      await hookHandleSubmitForm(inputValues);
    },
    [props, hookHandleSubmitForm],
  );

  const renderModal = useCallback(() => {
    const { activeModal } = state;

    if (!activeModal) {
      return null;
    }

    return (
      <div className={contactStyles.modal}>
        {activeModal === 'landlordInfo' && (
          <LandlordInfo isPopup={isPopup} onClickPhoneContact={handlePhoneClick} onCloseModal={handleSetActiveModal} />
        )}
      </div>
    );
  }, [state, isPopup, handlePhoneClick, handleSetActiveModal]);

  const { previousInquiryMessagePopup } = state;

  // Get form state from the hook, preferring prop overrides for error messages
  const {
    contactSubmitted,
    emailErrorMessage: hookEmailError,
    errorMessage,
    isLoading,
    phoneErrorMessage: hookPhoneError,
  } = formState;
  const emailErrorMessage = props.emailErrorMessage || hookEmailError;
  const phoneErrorMessage = props.phoneErrorMessage || hookPhoneError;

  const {
    address,
    aliasEncoded,
    maloneLotIdEncoded,
    trusted,
    contact,
    listedBy,
    details,
    isApartmentBldg,
    waitlisted,
  } = listing;

  const css = cx(className, !hdpV3Form ? containerStyles({ isPopup }) : undefined);
  const CustomForm = customForm || null;

  if (isEmpty(contact)) {
    return null;
  }

  const hasConversation = Boolean(conversations[aliasEncoded]);
  const conversation = conversations[aliasEncoded];
  const satelliteEnabled =
    hasConversation && conversation && typeof conversation === 'object' && 'satelliteEnabled' in conversation
      ? (conversation as { satelliteEnabled: boolean }).satelliteEnabled
      : false;

  return (
    <div className={css} data-testid="contact-box-0">
      <div
        className={
          !hdpV3Form
            ? contentWrapperStyles({ isInline: !isPopup })
            : cx(
                { 'HdpForm-Contact-content-wrapper': !hdpV3Form },
                {
                  'HdpForm-Contact-content-wrapper-inline': !isPopup && !hdpV3Form,
                },
              )
        }
        data-testid="contact-box-1"
      >
        {contact.displayContactBox ? (
          <div
            className={
              !hdpV3Form
                ? contentStyles({ isPopup })
                : cx(
                    { 'HdpForm-Contact-content': !hdpV3Form },
                    {
                      'HdpForm-Contact-content-popup': isPopup,
                    },
                  )
            }
            data-testid="contact-box-2"
          >
            <ContactOverlay
              overlayOffset={hdpV3Form ? '61px' : undefined}
              contactSubmitted={contactSubmitted}
              hasConversation={hasConversation}
              previousInquiryMessagePopup={previousInquiryMessagePopup}
              satelliteEnabled={satelliteEnabled}
              listing={listing}
              shouldLockFocus={!!isPopup}
            />
            {!hdpV3Form && (
              <ContactListedBy
                address={address}
                details={details}
                isApartmentBldg={isApartmentBldg}
                isPopup={isPopup}
                listedBy={listedBy}
                maloneLotIdEncoded={maloneLotIdEncoded}
                onSetActiveModal={handleSetActiveModal}
                onClickPhoneContact={handlePhoneClick}
                trusted={trusted}
                waitlisted={waitlisted}
              />
            )}
            <ChatButton listing={listing} showCopy />
            {hasConversation && (
              <div className={contactStyles.satelliteWrapper}>
                <SatelliteMsgStatus
                  className={cx('SatelliteMsgStatus-inContactBox', {
                    'SatelliteMsgStatus-inContactBoxPopup': isPopup,
                  })}
                />
              </div>
            )}
            {(!hasConversation || !satelliteEnabled) && CustomForm && (
              <>
                <CustomForm
                  isCachedDataPending={isCachedDataPending ?? (userInquiryDataCache.isPending || false)}
                  clientLoaded={clientLoaded}
                  contactSubmitted={contactSubmitted}
                  emailErrorMessage={emailErrorMessage}
                  errorMessage={errorMessage}
                  isLoading={isLoading}
                  isMobile={isMobile}
                  isPopup={isPopup}
                  listing={listing}
                  onSubmitForm={handleSubmitForm}
                  phoneErrorMessage={phoneErrorMessage}
                  user={user}
                  userInquiryDataCache={userInquiryDataCache}
                />
              </>
            )}

            {(!hasConversation || !satelliteEnabled) && !CustomForm && (
              <ContactForm
                isCachedDataPending={isCachedDataPending ?? (userInquiryDataCache.isPending || false)}
                clientLoaded={clientLoaded}
                contactSubmitted={contactSubmitted}
                emailErrorMessage={emailErrorMessage}
                errorMessage={errorMessage}
                isLoading={isLoading}
                isMobile={isMobile}
                isPopup={isPopup ?? false}
                listing={listing}
                onSetActiveModal={handleSetActiveModal}
                onSubmitForm={handleSubmitForm}
                onClickPhoneContact={handlePhoneClick}
                phoneErrorMessage={phoneErrorMessage}
                user={user as { loggedIn?: boolean; info?: { firstName?: string; lastName?: string } }}
                userInquiryDataCache={
                  userInquiryDataCache as {
                    text?: string;
                    name?: string;
                    phone?: string;
                    email?: string;
                    isPending?: boolean;
                  }
                }
                renderModal={renderModal}
                {...unitDetails}
              />
            )}
            {renderModal()}
          </div>
        ) : (
          <div className={!hdpV3Form ? contentStyles({ isPopup: false }) : 'HdpForm-Contact-content'}>
            <ContactListedBy
              address={address}
              details={details}
              isApartmentBldg={isApartmentBldg}
              isPopup={isPopup}
              listedBy={listedBy}
              maloneLotIdEncoded={maloneLotIdEncoded}
              onSetActiveModal={handleSetActiveModal}
              trusted={trusted}
              waitlisted={waitlisted}
            />
            This property did not provide a contact email address.
            {renderModal()}
          </div>
        )}
      </div>
      <div
        className={
          !hdpV3Form
            ? disclaimerStyles({ isPopup })
            : cx('HdpForm-Contact-disclaimer', {
                'HdpForm-Contact-disclaimer-popup': isPopup,
              })
        }
      >
        <Disclaimer linkType={isPopup ? 'primary' : 'inverted'} />
      </div>
    </div>
  );
};

export { Contact }; // destructured export for test
export default withRouter(Contact);
